import SDK from '../../Components/SDK';
import logger from '../../Helpers/logger';
import {
    applyLiveLocationEvent,
    markLiveLocationsStale,
    selectLiveLocation,
    upsertLiveLocations,
} from '../../slice/liveLocationSlice';

// Cards drawn in the same frame are fetched in one batch call.
const BATCH_DELAY_MS = 30;
const EVENT_ENDED = 'ended';
const queuedMessageIds = new Set();
const loadingMessageIds = new Set();
let batchTimer = null;

/**
 * Asks for the live share behind a message; requests made in the same frame share one call.
 *
 * @param {string} messageId Live location message.
 */
export const requestLiveLocation = messageId => dispatch => {
    if (!messageId || loadingMessageIds.has(messageId)) return;
    queuedMessageIds.add(messageId);
    if (batchTimer) return;
    batchTimer = setTimeout(() => {
        batchTimer = null;
        const messageIds = [...queuedMessageIds];
        queuedMessageIds.clear();
        dispatch(loadLiveLocations(messageIds));
    }, BATCH_DELAY_MS);
};

/**
 * Loads every share still active in a chat, for the pins of the live location pop-up.
 *
 * @param {string} chatJid JID of the chat on screen.
 * @returns {Promise<string[]>} Message ids of the active shares, empty on failure.
 */
export const loadActiveLiveLocations = chatJid => async dispatch => {
    if (!chatJid) return [];
    try {
        const response = await SDK.getActiveLiveLocations(chatJid);
        if (response?.statusCode === 200) {
            const shares = response.data || [];
            dispatch(upsertLiveLocations(shares));
            return shares.map(share => share.messageId);
        }
        logger.warn('Active live locations not loaded', { message: response?.message });
    } catch (error) {
        logger.error('Active live locations load threw', { error });
    }
    return [];
};

/**
 * Applies a live location event from the SDK listener. Position values are never logged.
 *
 * @param {object} event Event from liveLocationEventListener.
 */
export const receiveLiveLocationEvent = event => (dispatch, getState) => {
    if (!event?.messageId) return;
    const isKnown = Boolean(selectLiveLocation(getState(), event.messageId));
    dispatch(applyLiveLocationEvent({ ...event, receivedAt: Date.now() }));
    // The server copy carries the start and exact end times an event does not.
    if (!isKnown || event.event === EVENT_ENDED) dispatch(markLiveLocationsStale([event.messageId]));
};

const loadLiveLocations = messageIds => async dispatch => {
    // The SDK refuses an empty list without calling the server.
    if (!messageIds.length) return;
    messageIds.forEach(messageId => loadingMessageIds.add(messageId));
    try {
        const response = await SDK.getLiveLocations(messageIds);
        if (response?.statusCode === 200) {
            dispatch(upsertLiveLocations(response.data || []));
        } else {
            logger.warn('Live locations not loaded', { count: messageIds.length, message: response?.message });
        }
    } catch (error) {
        logger.error('Live locations load threw', { count: messageIds.length, error });
    } finally {
        messageIds.forEach(messageId => loadingMessageIds.delete(messageId));
    }
};
